import React, { useEffect, useRef } from "react";
import { Card, Row, Col, Statistic, Table, Divider } from "antd";
import Chart from "chart.js/auto";

const SalesAndCostDashboard = ({ data, loading }) => {
  const { salesData, costsData, refundsData } = data || {};

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(value);
  };

  const salesColumns = [
    {
      title: "",
      dataIndex: "type",
      key: "type",
    },
    {
      title: "Orders",
      dataIndex: "count",
      key: "count",
    },
    {
      title: "Sales",
      dataIndex: "sales",
      key: "sales",
      render: (value) => formatCurrency(value),
    },
    {
      title: "AOV",
      dataIndex: "aov",
      key: "aov",
      render: (value) => formatCurrency(value),
    },
    {
      title: "Order Percent",
      dataIndex: "orderPerc",
      key: "orderPerc",
      render: (value) => (value ? `${value.toFixed(2)}%` : "0%"),
    },
    {
      title: "Sales Percent",
      dataIndex: "salesPerc",
      key: "salesPerc",
      render: (value) => (value ? `${value.toFixed(2)}%` : "0%"),
    },
  ];

  const chartRefs = {
    totalCost: useRef(null),
    fixedCost: useRef(null),
    variableCost: useRef(null),
  };

  const createChart = (canvasRef, data, labels, colors) => {
    if (canvasRef.current) {
      const existingChart = Chart.getChart(canvasRef.current);
      if (existingChart) existingChart.destroy();

      return new Chart(canvasRef.current, {
        type: "pie",
        data: {
          labels,
          datasets: [
            {
              data,
              backgroundColor: colors,
            },
          ],
        },
        options: { responsive: true },
      });
    }
  };

  useEffect(() => {
    const filterData = (data, labels) => {
      return labels
        ?.map((label, index) =>
          label !== "Total" ? { label, amount: data[index] } : null
        )
        ?.filter(Boolean);
    };

    const filteredFixed = filterData(
      costsData?.fixedBreakdown?.map((item) => item.amount),
      costsData?.fixedBreakdown?.map((item) => item.name)
    );

    const filteredVariable = filterData(
      costsData?.breakdown?.map((item) => item.amount),
      costsData?.breakdown?.map((item) => item.name)
    );

    createChart(
      chartRefs.totalCost,
      [costsData?.fixed, costsData?.variable],
      ["Fixed", "Variable"],
      ["#FF6384", "#36A2EB"]
    );

    createChart(
      chartRefs.fixedCost,
      filteredFixed?.map((item) => item.amount),
      filteredFixed?.map((item) => item.label),
      ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0", "#9966FF", "#FF9F40"]
    );

    createChart(
      chartRefs.variableCost,
      filteredVariable?.map((item) => item.amount),
      filteredVariable?.map((item) => item.label),
      ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0", "#9966FF", "#FF9F40"]
    );
  }, [loading]);

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={17}>
          <Card title="Sales" loading={loading}>
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Statistic
                  title="Cross Sales Count"
                  value={salesData?.crossSales?.count}
                  valueStyle={{ fontSize: 14 }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Cross Sales"
                  value={salesData?.crossSales?.amount}
                  formatter={(value) => formatCurrency(value)}
                  valueStyle={{ fontSize: 14 }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Cross Sales Percentage"
                  value={salesData?.crossSales?.percentage?.toFixed(2)}
                  suffix="%"
                  valueStyle={{ fontSize: 14 }}
                />
              </Col>
            </Row>
            <hr />
            <Table
              dataSource={salesData?.orders}
              columns={salesColumns}
              pagination={false}
              rowKey="type"
              size="small"
            />
          </Card>
        </Col>

        <Col xs={24} md={7}>
          <Card title="Refunds" loading={loading}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="Refunds"
                  value={refundsData?.amount}
                  formatter={(value) => formatCurrency(value)}
                  valueStyle={{ fontSize: 16 }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Orders Refunded"
                  value={refundsData?.ordersRefunded}
                  valueStyle={{ fontSize: 16 }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Refund Sale Ratio"
                  value={refundsData?.refundSaleRatio?.toFixed(2)}
                  suffix="%"
                  valueStyle={{ fontSize: 16 }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Refund Order Ratio"
                  value={refundsData?.refundOrderRatio?.toFixed(2)}
                  suffix="%"
                  valueStyle={{ fontSize: 16 }}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
      <Card title={"Costs"} className="mt-1">
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={8}>
            <Card loading={loading}>
              <Table
                dataSource={[
                  {
                    key: "1",
                    total: costsData?.total,
                    fixed: costsData?.fixed,
                    variable: costsData?.variable,
                  },
                  {
                    key: "2",
                    total: "Percentage",
                    fixed:
                      costsData?.total > 0
                        ? ((costsData?.fixed * 100) / costsData?.total).toFixed(
                            2
                          )
                        : "0.00",

                    variable:
                      costsData?.total > 0
                        ? (
                            (costsData?.variable * 100) /
                            costsData?.total
                          ).toFixed(2)
                        : "0.00",
                  },
                ]}
                columns={[
                  {
                    title: "Total Cost",
                    dataIndex: "total",
                    key: "total",
                    render: (v) => (v === "Percentage" ? v : formatCurrency(v)),
                  },
                  {
                    title: "Fixed Cost",
                    dataIndex: "fixed",
                    key: "fixed",
                    render: (v, record) => record.total === "Percentage" ? record.fixed + "%": formatCurrency(v),
                  },
                  {
                    title: "Variable Cost",
                    dataIndex: "variable",
                    key: "variable",
                    render: (v, record) => record.total === "Percentage" ? record.variable + "%": formatCurrency(v),
                  },
                ]}
                pagination={false}
                size="small"
              />
              <canvas ref={chartRefs?.totalCost} />
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card loading={loading}>
              <Table
                dataSource={costsData?.fixedBreakdown?.map((item, index) => ({
                  key: index,
                  name: item.name,
                  amount: item.amount,
                  percentage:
                    costsData?.fixed !== 0
                      ? ((item.amount * 100) / costsData?.fixed)?.toFixed(2) +
                        "%"
                      : "0%",
                }))}
                columns={[
                  { title: "Fixed Cost", dataIndex: "name", key: "name" },
                  {
                    title: "Amount",
                    dataIndex: "amount",
                    key: "amount",
                    render: formatCurrency,
                  },
                  {
                    title: "Percentage",
                    dataIndex: "percentage",
                    key: "percentage",
                  },
                ]}
                pagination={false}
                size="small"
              />
              <canvas ref={chartRefs?.fixedCost} />
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card loading={loading}>
              <Table
                dataSource={costsData?.breakdown?.map((item, index) => ({
                  key: index,
                  name: item.name,
                  amount: item.amount,
                  percentage:
                    costsData?.variable !== 0
                      ? ((item.amount * 100) / costsData?.variable)?.toFixed(
                          2
                        ) + "%"
                      : "0%",
                }))}
                columns={[
                  { title: "Variable Cost", dataIndex: "name", key: "name" },
                  {
                    title: "Amount",
                    dataIndex: "amount",
                    key: "amount",
                    render: formatCurrency,
                  },
                  {
                    title: "Percentage",
                    dataIndex: "percentage",
                    key: "percentage",
                  },
                ]}
                pagination={false}
                size="small"
              />
              <canvas ref={chartRefs?.variableCost} />
            </Card>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default SalesAndCostDashboard;
